import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo } from '@/mocks/articles';

const FORM_URL = 'https://readdy.ai/api/form/d9u4o0lca23r0tr1aqgg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        params.append(key, value as string);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.introduction}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  品牌使命
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  联系我们
                </h2>
                <ul className="space-y-3 text-sm md:text-base text-foreground-600">
                  <li className="flex items-start gap-3">
                    <i className="ri-map-pin-line mt-0.5 text-primary-500"></i>
                    <span>{aboutInfo.contact.address}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-phone-line text-primary-500"></i>
                    <span>{aboutInfo.contact.phone}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-mail-line text-primary-500"></i>
                    <span>{aboutInfo.contact.email}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-wechat-line text-primary-500"></i>
                    <span>微信公众号：{aboutInfo.contact.wechat}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌荣誉
            </h2>
            <div className="space-y-4">
              {aboutInfo.honors.map((honor, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 bg-background-50 rounded-lg p-4 md:p-5"
                >
                  <div className="w-10 h-10 bg-accent-100 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 font-medium">{honor.year}</span>
                    <p className="text-sm md:text-base text-foreground-800 mt-1">{honor.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              给我们留言
            </h2>
            <form
              id="about-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
              action={FORM_URL}
              method="POST"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字</p>
              </div>

              <input
                name="phone_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-honeypot"
              />

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 text-center">
                  感谢您的留言，我们会尽快回复！
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 text-center">
                  {formError || '提交失败，请稍后再试'}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}